import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { Box, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { Autocomplete } from '@mui/material';

import generator from 'generate-password-browser';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { useUniversities } from '../../hooks/universities';
import { Title } from '../../components/atoms/title';
import { partnerTypes } from '../../utils/partnerTypes';

const schema = yup
  .object({
    full_name: yup.string().required('O nome do parceiro é obrigatório'),
    name: yup.string().required('O usuário do parceiro é obrigatório'),
    password: yup.string().min(8, 'A senha deve ter no mínimo 8 caracteres').required('A senha é obrigatória'),
  })
  .required();

export function Partners() {
  const { universities, countries } = useUniversities();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      full_name: '',
      name: '',
      password: generator.generate({
        length: 10,
        numbers: true,
      }),
    },

    resolver: yupResolver(schema),
  });

  const [loading, setLoading] = useState(false);

  const [type, setType] = useState('');
  const [university, setUniversity] = useState('');
  const [country, setCountry] = useState(null);

  useEffect(() => {
    setCountry(countries.find(c => c.name === 'Brazil')?.id);
  }, [countries]);

  const handleShareType = event => {
    const { value } = event.target;

    setType(value);
  };

  const handleChangeCountryId = (_e, value, reason) => {
    if (reason === 'clear') {
      setCountry(null);
      return;
    }

    if (value !== null) {
      setCountry(value.id);
    }
  };

  const handleCreatePartner = async formData => {
    try {
      setLoading(true);
      await api.post('/partners', {
        country_id: country,
        full_name: formData.full_name,
        locale: 'pt-br', // TODO - get from user
        name: formData.name,
        password: formData.password,
        timezone: 'America/Sao_Paulo', // TODO - get from user
        type,
        university,
      });

      queryClient.invalidateQueries({ queryKey: ['partners'] });

      reset();
      setValue(
        'password',
        generator.generate({
          length: 10,
          numbers: true,
        })
      );

      setUniversity('');
      setType('');

      toast('Afiliado criado com sucesso!', { type: 'success' });
    } catch (error) {
      toast('Erro ao criar afiliado', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item>
        <Title>Parceiros</Title>
      </Grid>

      <Grid item xs={12}>
        {!loading && (
          <>
            <Grid container spacing={2}>
              <Grid xs={12} md={6} item>
                <TextField
                  required
                  id='full_name'
                  name='full_name'
                  label='Nome'
                  fullWidth
                  variant='outlined'
                  {...register('full_name')}
                  error={!!errors.full_name}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <Typography variant='inherit' color='textSecondary'>
                  {errors.full_name?.message}
                </Typography>
              </Grid>

              <Grid xs={12} md={6} item>
                <TextField
                  required
                  id='name'
                  name='name'
                  label='Usuário'
                  fullWidth
                  variant='outlined'
                  {...register('name')}
                  error={!!errors.name}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <Typography variant='inherit' color='textSecondary'>
                  {errors.name?.message}
                </Typography>
              </Grid>

              <Grid xs={12} md={6} item>
                <TextField
                  required
                  id='password'
                  name='password'
                  label='Senha'
                  fullWidth
                  variant='outlined'
                  {...register('password')}
                  error={!!errors.password}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <Typography variant='inherit' color='textSecondary'>
                  {errors.password?.message}
                </Typography>
              </Grid>

              <Grid xs={12} md={6} item>
                <TextField select value={type} fullWidth variant='outlined' label='Tipo' onChange={handleShareType}>
                  {partnerTypes.map(item => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid xs={12} md={6} item>
                <Autocomplete
                  required
                  onChange={handleChangeCountryId}
                  options={countries}
                  getOptionLabel={option => option?.name}
                  defaultValue={{ name: 'Brazil', id: 1 }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  fullWidth
                  variant='outlined'
                  label='País'
                  renderInput={params => <TextField {...params} variant='outlined' label='País' />}
                />
              </Grid>

              <Grid xs={12} md={6} item>
                {country && (
                  <Autocomplete
                    id='combo-box-demo'
                    options={universities.filter(uni => {
                      if (country === 1) {
                        return uni.state_id <= 27;
                      }
                      if (country === 2) {
                        return uni.state_id > 27;
                      }
                      return false;
                    })}
                    getOptionLabel={option => option.name}
                    getOptionKey={option => option.id}
                    onChange={(_e, value) => {
                      if (value?.name) {
                        setUniversity(value.name);
                      } else {
                        setUniversity(null);
                      }
                    }}
                    renderInput={params => <TextField {...params} placeholder='Universidade' variant='outlined' />}
                  />
                )}
              </Grid>
            </Grid>
            <Box m='auto' mt={3} mb={3}>
              <Button
                type='submit'
                fullWidth
                variant='contained'
                color='primary'
                onClick={handleSubmit(handleCreatePartner)}
              >
                Criar
              </Button>
            </Box>
          </>
        )}
        {loading && (
          <Grid item>
            <CircularProgress />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}
